import { useState } from 'react'
import { router } from '@inertiajs/react'
import { MeetingSectionNote } from '../../types'
import ConfirmModal from '../shared/ConfirmModal'

interface Props {
  meetingId: number
  sectionId: number
  notes: MeetingSectionNote[]
  readOnly?: boolean
  label?: string
}

// Task #607: the Scoreboard section keeps its notes as individual records that
// carry forward into every future meeting until someone deletes one. Each
// meeting owns its own copy, so deleting here only stops the note from
// carrying forward — past meetings and their recaps are untouched.
//
// The list is rendered straight from the `sections` prop (no local mirror) so
// the meeting heartbeat's prop re-serialization keeps it in sync instead of
// fighting it; add/delete partial-reload just that prop.
export default function ScoreboardSectionNotes({
  meetingId,
  sectionId,
  notes,
  readOnly = false,
  label = 'Section Notes',
}: Props) {
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<MeetingSectionNote | null>(null)
  const [deleting, setDeleting] = useState(false)
  // Task #619: in-place editing. Only the id is tracked (never the note object)
  // so the meeting heartbeat's prop re-serialization can't clobber the draft or
  // silently swap which note is being edited.
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  const ordered = [...notes].sort((a, b) => (a.position - b.position) || (a.id - b.id))

  const startEditing = (note: MeetingSectionNote) => {
    if (readOnly) return
    setError(null)
    setEditingId(note.id)
    setEditDraft(note.body)
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditDraft('')
  }

  const saveEdit = (note: MeetingSectionNote) => {
    const body = editDraft.trim()
    if (savingEdit) return
    if (!body || body === note.body) {
      cancelEditing()
      return
    }

    setSavingEdit(true)
    setError(null)
    router.patch(`/meetings/${meetingId}/sections/${sectionId}/notes/${note.id}`, { body }, {
      preserveScroll: true,
      preserveState: true,
      only: ['sections'],
      onSuccess: () => cancelEditing(),
      onError: () => setError('Could not save that note — try again.'),
      onFinish: () => setSavingEdit(false),
    })
  }

  const addNote = () => {
    const body = draft.trim()
    if (!body || submitting) return

    setSubmitting(true)
    setError(null)
    router.post(`/meetings/${meetingId}/sections/${sectionId}/notes`, { body }, {
      preserveScroll: true,
      preserveState: true,
      only: ['sections'],
      onSuccess: () => setDraft(''),
      onError: () => setError('Could not add that note — try again.'),
      onFinish: () => setSubmitting(false),
    })
  }

  const confirmDelete = () => {
    if (!pendingDelete) return
    setDeleting(true)
    router.delete(`/meetings/${meetingId}/sections/${sectionId}/notes/${pendingDelete.id}`, {
      preserveScroll: true,
      preserveState: true,
      only: ['sections'],
      onError: () => setError('Could not delete that note — try again.'),
      onFinish: () => {
        setDeleting(false)
        setPendingDelete(null)
      },
    })
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-text-muted">{label}</h4>
        {error && <span role="alert" className="text-xs text-pinnacle-red">{error}</span>}
      </div>

      {ordered.length > 0 ? (
        <ul className="divide-y divide-stone/10 rounded-lg border border-stone/30 bg-white">
          {ordered.map(note => (
            <li key={note.id} className="flex items-start justify-between gap-3 px-3 py-2">
              {editingId === note.id ? (
                <>
                  <input
                    type="text"
                    autoFocus
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        saveEdit(note)
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        cancelEditing()
                      }
                    }}
                    aria-label={`Edit note: ${note.body}`}
                    className="flex-1 rounded-lg border border-stone/30 bg-white px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red/40"
                  />
                  <button
                    type="button"
                    onClick={() => saveEdit(note)}
                    disabled={savingEdit || editDraft.trim().length === 0}
                    className="shrink-0 px-3 py-1 text-sm font-semibold text-white bg-pinnacle-red rounded-lg hover:bg-pinnacle-red-dark transition-colors disabled:opacity-50"
                  >
                    {savingEdit ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditing}
                    disabled={savingEdit}
                    className="shrink-0 px-2 py-1 text-sm text-text-muted hover:text-text-primary transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {readOnly ? (
                    <p className="text-sm text-text-primary whitespace-pre-wrap flex-1">{note.body}</p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditing(note)}
                      aria-label={`Edit note: ${note.body}`}
                      className="text-sm text-text-primary whitespace-pre-wrap flex-1 text-left hover:text-pinnacle-red transition-colors"
                    >
                      {note.body}
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => setPendingDelete(note)}
                      aria-label={`Delete note: ${note.body}`}
                      className="shrink-0 text-text-muted hover:text-pinnacle-red transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-muted italic">
          {readOnly
            ? 'No notes were captured for this section.'
            : 'No notes yet. Notes added here carry over to future meetings until you delete them.'}
        </p>
      )}

      {!readOnly && (
        <>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addNote()
                }
              }}
              placeholder="Add a note that carries over to future meetings…"
              aria-label="New scoreboard note"
              className="flex-1 rounded-lg border border-stone/30 bg-white px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red/40"
            />
            <button
              type="button"
              onClick={addNote}
              disabled={submitting || draft.trim().length === 0}
              className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded-lg hover:bg-pinnacle-red-dark transition-colors disabled:opacity-50"
            >
              {submitting ? 'Adding…' : 'Add'}
            </button>
          </div>
          {ordered.length > 0 && (
            <p className="text-xs text-text-muted">Click a note to edit it. These notes carry over to future meetings until deleted.</p>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={pendingDelete !== null}
        title="Are you sure?"
        message={
          pendingDelete
            ? `Delete this note? It will no longer carry over to future meetings.`
            : ''
        }
        confirmLabel="Yes"
        cancelLabel="No"
        submitting={deleting}
        onConfirm={confirmDelete}
        onClose={() => {
          if (!deleting) setPendingDelete(null)
        }}
      />
    </div>
  )
}
